import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  X,
  Lock,
  ArrowUpRight,
  CheckCircle,
  Sparkles,
  Crown,
  Loader,
} from "lucide-react";

// Feature display names
const FEATURE_NAMES = {
  fingerprint_capture: "Fingerprint Capture",
  fd258_cards: "FD-258 Card Generation",
  fd1164_cards: "FD-1164 Card Generation",
  local_database: "Local Database Storage",
  encryption: "AES-256 Encryption",
  id_verification: "ID Verification (OCR)",
  offline_mode: "USB/Offline Mode",
  cloud_sync: "Cloud Storage & Sync",
  multi_device: "Multi-Device Access",
  ebts_export: "EBTS Export",
  wsq_compression: "WSQ Compression",
  real_time_backup: "Real-time Backup",
  lan_installation: "LAN Installation",
  unlimited_users: "Unlimited Users",
  custom_branding: "Custom Branding",
  white_label: "White-Label",
  api_access: "API Access",
  dedicated_support: "Dedicated Support",
  sla_guarantee: "SLA Guarantee",
  on_site_training: "On-Site Training",
  priority_support: "Priority Support (24hr)",
};

// Tier pricing info
const TIER_INFO = {
  professional: {
    name: "Professional",
    monthlyPrice: 99,
    annualPrice: 999,
    icon: Sparkles,
    color: "blue",
    highlights: [
      "FD-1164 Card Generation",
      "Cloud Storage & Sync",
      "Multi-Device Access (5)",
      "EBTS Export",
      "24hr Support Response",
    ],
  },
  enterprise: {
    name: "Enterprise",
    monthlyPrice: 399,
    annualPrice: 3999,
    icon: Crown,
    color: "purple",
    highlights: [
      "Everything in Professional",
      "LAN Installation",
      "Unlimited Users & Workstations",
      "White-Label Branding",
      "API Access",
      "4hr Support Response",
    ],
  },
};

const UpgradeModal = ({ isOpen, onClose, feature, requiredTier }) => {
  const [upgradeOptions, setUpgradeOptions] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && requiredTier) {
      fetchUpgradeOptions();
    }
  }, [isOpen, requiredTier]);

  const fetchUpgradeOptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/subscription/upgrade-options");
      setUpgradeOptions(response.data);
    } catch (err) {
      console.error("Failed to fetch upgrade options:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const featureName = FEATURE_NAMES[feature] || feature?.replace(/_/g, " ");
  const tierInfo = TIER_INFO[requiredTier] || TIER_INFO.professional;
  const TierIcon = tierInfo.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="upgrade-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-slate-800 border border-white/20 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-white/50 hover:text-white transition z-10"
          data-testid="close-upgrade-modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with locked icon */}
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 p-6 text-center border-b border-white/10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/20 rounded-full mb-4">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-xl font-heading font-bold text-white mb-2">
            Feature Locked
          </h2>
          <p className="text-blue-200/70 text-sm">
            <span className="text-white font-medium">{featureName}</span> requires a{" "}
            <span className="text-amber-400 font-medium">{tierInfo.name}</span> plan or higher.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-blue-200">
              <Loader className="w-6 h-6 animate-spin mr-2" />
              Loading upgrade options...
            </div>
          ) : (
            <>
              {/* Upgrade card */}
              <div className={`bg-${tierInfo.color}-500/10 border border-${tierInfo.color}-400/30 rounded-xl p-4 mb-4`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 bg-${tierInfo.color}-500 rounded-lg flex items-center justify-center`}>
                    <TierIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{tierInfo.name}</h3>
                    <p className="text-sm text-blue-200/70">
                      ${tierInfo.monthlyPrice}/mo or ${tierInfo.annualPrice}/yr
                    </p>
                  </div>
                </div>

                <ul className="space-y-2">
                  {tierInfo.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-white/80">
                      <CheckCircle className={`w-4 h-4 text-${tierInfo.color}-400 flex-shrink-0`} />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Upgrade info */}
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-white mb-2">How upgrading works:</h4>
                <ol className="text-xs text-blue-200/70 space-y-1 list-decimal list-inside">
                  <li>Purchase a new plan on our pricing page</li>
                  <li>You&apos;ll receive a new license key via email</li>
                  <li>Enter the new key in Settings → License</li>
                  <li>New features unlock immediately!</li>
                </ol>
                <p className="text-xs text-emerald-400 mt-2">
                  No new hardware needed - same USB, more features.
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition"
                >
                  Maybe Later
                </button>
                <Link
                  to="/license"
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium rounded-lg hover:from-cyan-400 hover:to-blue-400 transition"
                  data-testid="upgrade-now-btn"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  View Plans
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
